<template>
    <!-- Generic tree select: nodes of any depth in, selected leaf keys out. It
         knows nothing about points or sites; wrappers such as PointSelectTree
         map their data to nodes and back.

         Nodes: [{ key, label, children?, status? }]. Keys must be unique across
         the whole tree. A node with no `children` and no `status` is a leaf.
         `status: 'loading' | 'error'` shows a status in place of the node's
         children; the error status has a Retry button that emits retry(key).
         Replace `nodes` to update it; the tree is indexed once per new array. -->
    <div class="overflow-clip rounded-md border border-gray-200 bg-white">
        <!-- On a phone the header stacks: the search takes its own full-width row,
             then Select all / Expand all share a 50/50 row below, each a full 48px
             (~8mm) tap target. From sm up it is one inline row. -->
        <div class="flex flex-col gap-2 border-b border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:gap-3">
            <input  v-model="searchQuery"
                    type="text"
                    :placeholder="searchPlaceholder"
                    class="search-input w-full min-h-[48px] rounded-md border border-gray-300 bg-white px-3 py-2 text-base transition-colors focus:border-blue-500 focus:ring-blue-500 sm:min-h-0 sm:flex-1 sm:text-sm"
            >
            <div class="flex gap-2 sm:contents">
                <button type="button"
                        :disabled="allSelected || visibleLeafTotal === 0"
                        class="inline-flex min-h-[48px] flex-1 items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white sm:min-h-0 sm:flex-none"
                        @click="selectAllVisible"
                >
                    {{ searching ? 'Select all matching' : 'Select all' }}
                </button>
                <button type="button"
                        class="inline-flex min-h-[48px] flex-1 items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 sm:min-h-0 sm:flex-none"
                        @click="toggleAllExpanded"
                >
                    {{ allExpanded ? 'Collapse all' : 'Expand all' }}
                </button>
            </div>
        </div>

        <!-- Always occupies its row, even at zero selections, so the first click
             does not push every row down from under the cursor. -->
        <div    class="flex h-12 items-center justify-between gap-3 border-b border-gray-100 px-4 transition-colors"
                :class="selectedCount > 0 ? 'bg-blue-50' : 'bg-gray-50'"
        >
            <span   class="text-sm font-medium"
                    :class="selectedCount > 0 ? 'text-blue-900' : 'text-gray-500'">
                {{ selectedCount > 0
                    ? `${selectedCount} ${selectedCount === 1 ? itemNoun.one : itemNoun.many} selected`
                    : `No ${itemNoun.many} selected` }}
            </span>
            <button v-if="selectedCount > 0"
                    type="button"
                    class="-mr-2 inline-flex min-h-[48px] items-center gap-1 rounded-md px-2 text-sm font-medium text-blue-700 transition-colors hover:text-blue-900 sm:mr-0 sm:min-h-0 sm:px-0"
                    @click="clearSelection"
            >
                <tabler-icon name="x" size-class="h-3.5 w-3.5" :stroke-width="2"></tabler-icon>
                Clear
            </button>
        </div>

        <div    v-if="loading && rows.length === 0"
                class="flex min-h-64 items-center justify-center p-8 text-sm text-gray-500">
            Loading...
        </div>

        <div    v-else-if="rows.length === 0"
                class="flex min-h-64 items-center justify-center p-8">
            <div class="flex flex-col items-center justify-center space-y-0.5">
                <tabler-icon name="inbox" size-class="mb-2 h-8 w-8 text-gray-500"></tabler-icon>
                <span class="text-base font-medium">{{ searching ? noMatchTitle : emptyTitle }}</span>
                <span class="text-sm text-gray-500">{{ searching ? noMatchText : emptyText }}</span>
            </div>
        </div>

        <!-- One flat list; indent comes from depth, so any depth works. Every row
             has the same fixed height (48px on a phone, h-11 from sm up) and long
             labels are cut with an ellipsis, so row heights never vary. -->
        <ul v-else
            ref="list"
            role="tree"
            aria-multiselectable="true"
            class="m-0 max-h-96 list-none overflow-y-auto p-0"
            @scroll="onScroll"
        >
            <li v-if="padTop > 0" aria-hidden="true" :style="{height: `${padTop}px`}"></li>
            <li v-for="row in windowRows"
                :key="row.key"
                role="treeitem"
                :aria-level="row.depth + 1"
                :aria-expanded="row.isLeaf ? undefined : String(rowExpanded(row))"
                :aria-checked="ariaChecked(row)"
                class="tree-row flex h-[48px] cursor-pointer select-none items-center gap-1 border-b border-gray-100 pr-4 hover:bg-gray-100 sm:h-11 sm:gap-2"
                :class="rowClass(row)"
                :style="{'--depth': row.depth}"
                @click="row.isLeaf ? null : toggleExpanded(row)"
            >
                <!-- The chevron gets its own w-11 hit zone on a phone; a leaf gets an
                     empty spacer of the same width so checkboxes line up. -->
                <span class="flex h-full w-11 shrink-0 items-center justify-center text-gray-400 sm:w-6">
                    <template v-if="! row.isLeaf">
                        <tabler-icon v-if="rowExpanded(row)" name="chevronDown" size-class="h-4 w-4"></tabler-icon>
                        <tabler-icon v-else name="chevronRight" size-class="h-4 w-4"></tabler-icon>
                    </template>
                </span>
                <!-- The label fills the row height so the select tap area is the
                     full row, not only the checkbox. -->
                <label  class="m-0 flex h-full min-w-0 flex-1 cursor-pointer items-center gap-2"
                        :title="row.label"
                        @click.stop>
                    <input  type="checkbox"
                            class="h-5 w-5 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 sm:h-4 sm:w-4"
                            :disabled="leafCount(row) === 0"
                            :checked="state(row) === 'all'"
                            :indeterminate.prop="state(row) === 'some'"
                            @change="toggleNode(row, $event.target.checked)"
                    >
                    <span class="truncate">
                        <slot name="label" :node="row.node" :row="row">{{ row.label }}</slot>
                    </span>
                    <span   v-if="! row.isLeaf && row.status === null"
                            class="shrink-0 text-xs font-normal text-gray-500">({{ leafCount(row) }})</span>
                </label>
                <span   v-if="row.status === 'loading'"
                        class="shrink-0 text-xs text-gray-500">Loading...</span>
                <template v-else-if="row.status === 'error'">
                    <span class="shrink-0 text-xs text-red-700">Failed to load</span>
                    <button type="button"
                            class="shrink-0 text-sm font-medium text-blue-700 hover:text-blue-900"
                            @click.stop="$emit('retry', row.key)"
                    >Retry</button>
                </template>
            </li>
            <li v-if="padBottom > 0" aria-hidden="true" :style="{height: `${padBottom}px`}"></li>
        </ul>
    </div>
</template>

<script>
import TablerIcon from "@/components/elements/TablerIcon.vue"
import _ from "lodash"
import {toRaw} from "vue"
import {
    applySelection,
    buildRows,
    leafKeysInRange,
    leafPrefixes,
    nodeLeafCount,
    nodeState,
    normalizeTree,
    searchMask,
    windowRange,
} from "./treeSelectIndex"

export default {
    name: "TreeSelect",
    components: {TablerIcon},
    emits: ['update:modelValue', 'retry'],
    props: {
        // Selected leaf keys.
        modelValue: {
            type: Array,
            default: () => ([]),
        },
        // [{ key, label, children?, status? }], any depth.
        nodes: {
            type: Array,
            default: () => ([]),
        },
        expandedByDefault: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        searchPlaceholder: {
            type: String,
            default: 'Search',
        },
        // Milliseconds to wait after typing before filtering. The playground sets 0
        // so its timing readout measures the filter itself.
        searchDebounce: {
            type: Number,
            default: 150,
        },
        itemNoun: {
            type: Object,
            default: () => ({one: 'item', many: 'items'}),
        },
        emptyTitle: {
            type: String,
            default: 'Nothing to select',
        },
        emptyText: {
            type: String,
            default: '',
        },
        noMatchTitle: {
            type: String,
            default: 'No matches',
        },
        noMatchText: {
            type: String,
            default: 'Try a different search term.',
        },
    },
    data() {
        return {
            searchQuery: '',
            appliedQuery: '',
            // Expand all / Collapse all sets this once instead of writing a key per node.
            expandAll: null,
            // Per-node choices made since the last Expand all / Collapse all.
            overrides: {},
            // Windowing: only the rows in view (plus a buffer) are rendered.
            scrollTop: 0,
            rowHeight: 48,
            viewportHeight: 384,
        }
    },
    computed: {
        // toRaw: the nodes are read once here, so Vue's deep proxies would only add cost.
        index() {
            return normalizeTree(toRaw(this.nodes))
        },
        selectedSet() {
            return new Set(this.modelValue ?? [])
        },
        selectedCount() {
            return this.selectedSet.size
        },
        mask() {
            return searchMask(this.index.flat, this.appliedQuery)
        },
        searching() {
            return this.mask !== null
        },
        prefixes() {
            return leafPrefixes(this.index.leaves, this.index.leafFlatIndex, this.selectedSet, this.mask)
        },
        rows() {
            return buildRows(this.index.flat, this.mask, (entry) => this.isExpanded(entry))
        },
        visibleLeafTotal() {
            return this.prefixes.total[this.index.leaves.length]
        },
        allSelected() {
            return this.visibleLeafTotal > 0
                && this.prefixes.selected[this.index.leaves.length] === this.visibleLeafTotal
        },
        visibleWindow() {
            return windowRange(this.rows.length, this.scrollTop, this.rowHeight, this.viewportHeight, 10)
        },
        windowRows() {
            return this.rows.slice(this.visibleWindow.start, this.visibleWindow.end)
        },
        padTop() {
            return this.visibleWindow.padTop
        },
        padBottom() {
            return this.visibleWindow.padBottom
        },
        allExpanded() {
            return this.index.flat.every((entry) => entry.depth !== 0 || entry.isLeaf || this.isExpanded(entry))
        },
    },
    watch: {
        searchQuery(value) {
            if (this.searchDebounce <= 0) {
                this.appliedQuery = value
                return
            }

            this.applySearch(value)
        },
        appliedQuery() {
            this.scrollTop = 0
            if (this.$refs.list) {
                this.$refs.list.scrollTop = 0
            }
        },
        // The list sits behind v-else, so it can mount after the component (when data
        // arrives later). Measure it when it first appears.
        rows(newRows, oldRows) {
            if (oldRows.length === 0 && newRows.length > 0) {
                this.$nextTick(() => this.measure())
            }
        },
    },
    created() {
        this.applySearch = _.debounce((value) => {
            this.appliedQuery = value
        }, this.searchDebounce)
    },
    mounted() {
        // Row height comes from a rendered row, so it follows the breakpoint (48px on a phone,
        // h-11 from sm up) without hard-coding it.
        this.resizeObserver = new ResizeObserver(() => this.measure())
        // A list capped at max-h-96 keeps its size across the sm breakpoint, so the
        // observer alone would miss the row height change there.
        this.breakpoint = window.matchMedia('(min-width: 640px)')
        this.breakpoint.addEventListener('change', this.measure)
        this.$nextTick(() => this.measure())
    },
    beforeUnmount() {
        this.applySearch.cancel()
        this.resizeObserver?.disconnect()
        this.breakpoint?.removeEventListener('change', this.measure)
    },
    methods: {
        isExpanded(entry) {
            const override = this.overrides[entry.key]
            if (override !== undefined) {
                return override
            }

            return this.expandAll ?? this.expandedByDefault
        },
        // While searching, the path to each match is forced open.
        rowExpanded(row) {
            return (this.mask !== null && this.mask.reveal[row.index] === 1) || this.isExpanded(row)
        },
        toggleExpanded(row) {
            this.overrides = {...this.overrides, [row.key]: ! this.rowExpanded(row)}
        },
        toggleAllExpanded() {
            this.expandAll = ! this.allExpanded
            this.overrides = {}
        },
        leafCount(row) {
            return nodeLeafCount(row, this.prefixes)
        },
        state(row) {
            return nodeState(row, this.prefixes)
        },
        ariaChecked(row) {
            const state = this.state(row)
            if (state === 'some') {
                return 'mixed'
            }

            return state === 'all' ? 'true' : 'false'
        },
        rowClass(row) {
            if (row.depth === 0) {
                return ['font-medium text-gray-900', row.rootOrdinal % 2 === 0 ? 'bg-white' : 'bg-gray-50']
            }

            return row.isLeaf ? 'bg-white text-sm text-gray-700' : 'bg-white text-gray-800'
        },
        emitSelection(keys, selected) {
            this.$emit('update:modelValue', applySelection(this.selectedSet, keys, selected))
        },
        // Respects an active search: only the node's visible leaves change.
        toggleNode(row, selected) {
            this.emitSelection(
                leafKeysInRange(row.leafStart, row.leafEnd, this.index.leaves, this.index.leafFlatIndex, this.mask),
                selected
            )
        },
        selectAllVisible() {
            this.emitSelection(
                leafKeysInRange(0, this.index.leaves.length, this.index.leaves, this.index.leafFlatIndex, this.mask),
                true
            )
        },
        clearSelection() {
            this.$emit('update:modelValue', [])
        },
        measure() {
            const list = this.$refs.list
            if (! list) {
                return
            }

            // Re-observe only when the list element changes: observe() always reports the
            // current size, so re-arming here on every callback loops once per frame.
            if (this.observedList !== list) {
                this.resizeObserver.disconnect()
                this.resizeObserver.observe(list)
                this.observedList = list
            }
            this.viewportHeight = list.clientHeight || this.viewportHeight
            const row = list.querySelector('[role="treeitem"]')
            if (row && row.offsetHeight > 0) {
                this.rowHeight = row.offsetHeight
            }
        },
        onScroll(event) {
            this.scrollTop = event.target.scrollTop
        },
    },
}
</script>

<style scoped>
/* Indent by depth. Narrower steps on a phone, where width is short. */
.tree-row {
    padding-left: calc(0.25rem + var(--depth) * 1.25rem);
}

@media (min-width: 640px) {
    .tree-row {
        padding-left: calc(0.75rem + var(--depth) * 1.5rem);
    }
}

/*
 * Stop iOS Safari auto-zooming when the search field is focused on a phone.
 * Safari zooms any input whose font is under 16px. This app sets
 * `html { font-size: 14px }`, so Tailwind's `text-base` (1rem) is 14px. Pin a
 * hard 16px below sm. Desktop keeps the smaller Tailwind size.
 */
@media (max-width: 639px) {
    .search-input {
        font-size: 16px;
    }
}
</style>
